// Onion zones: how deep a cell sits inside the cube, which way each of its
// faces points relative to the shell it belongs to, and therefore which atlas
// tile that face samples. Ports `CubeOnion.cs` + the prefab selection in
// `Cube.AddSubCube` (`Cube.cs:3689-3717`).
//
// Pure math: no GL, no DOM — `grid.ts` consumes it while building the mesh.
//
// **`PartType` (Corner/Edge/Middle) is deliberately not ported.** In the
// original it exists only to pick among three pre-baked meshes whose UVs wrap
// grass around one, two or three outward faces. This port builds geometry per
// exposed *face*, so the same result falls out of the per-face rule below — a
// corner cell simply has three outward faces and gets three grass tiles. That
// is why this module is a handful of lines rather than a port of
// `CubeOnion.Init`'s four shell loops and its 26-entry rotation table.

/** Which way an exposed face points relative to its cell's shell. */
export type FaceRole = "outward" | "side" | "inward";

/** Atlas tiles actually in use; the shader's `TILES` table is indexed by these. */
export const Tile = {
  GrassTop: 0,
  GrassSide: 1,
  MagmaTop: 2,
  MagmaSide: 3,
  Stone: 4,
} as const;
export type TileId = (typeof Tile)[keyof typeof Tile];

/** Distance from either end of one axis, in cells. */
function axisDepth(c: number, n: number): number {
  return Math.min(c, n - 1 - c);
}

/**
 * Which way is "out" along one axis: -1 for a cell in the low half, +1 for the
 * high half, 0 for a cell sitting exactly on the middle of an odd-sized cube —
 * that axis then has no outward direction at all.
 */
function outwardSign(c: number, n: number): number {
  const high = n - 1 - c;
  if (c < high) return -1;
  if (c > high) return 1;
  return 0;
}

/**
 * Chebyshev distance from the cube's outer surface, in cells — the original's
 * `OnionData.ZoneDepth`, which it derives implicitly from which of
 * `CubeOnion.Init`'s shell passes (`d = 0 .. floor(n/2) - 1`) last wrote the
 * cell. On a 5-cube that gives depths 0 and 1, plus depth 2 for the lone centre
 * cell, which no shell reaches and which the original never draws.
 */
export function zoneDepth(x: number, y: number, z: number, n: number): number {
  return Math.min(axisDepth(x, n), axisDepth(y, n), axisDepth(z, n));
}

/**
 * The role of the face with outward normal `normal` on the cell at `(x, y, z)`.
 *
 * A face is *outward* only along an axis on which the cell sits at its own
 * shell's bound (`axisDepth === zoneDepth`) and only in the direction away from
 * the cube centre; the opposite face on that axis is *inward*. Every other
 * exposed face is tangential to the shell — a *side*.
 */
export function faceRole(
  x: number,
  y: number,
  z: number,
  n: number,
  normal: readonly [number, number, number],
): FaceRole {
  const axis = normal[0] !== 0 ? 0 : normal[1] !== 0 ? 1 : 2;
  const c = axis === 0 ? x : axis === 1 ? y : z;
  // An axis the cell is not at the bound of cannot carry the shell's outward
  // face: the block's "top" points along the shallowest axis, not this one.
  if (axisDepth(c, n) !== zoneDepth(x, y, z, n)) return "side";
  const out = outwardSign(c, n);
  if (out === 0) return "side";
  return normal[axis] === out ? "outward" : "inward";
}

/**
 * Atlas tile for a face, from its cell's depth and the face's role — this
 * port's stand-in for `CubeZoneDescriptions[ZoneDepth]`, whose three wired
 * entries bake exactly this mapping into their UVs (`unity-overview.md` §6):
 *
 * - zone 0, `1_subblock_1`: (1,2) top, (0,2) sides, (0,0) bottom — grass crust
 * - zone 1, `1_subblock_2`: (0,1) top, (1,1) sides, (0,0) bottom — magma
 * - zone 2, `1_subblock_3`: (0,1) on all six faces — a uniformly molten core
 *
 * The corner/edge wrap tiles (2,2) and (2,1) are unused: they belong to the
 * pre-baked corner and edge meshes the per-face rule above replaces.
 */
export function tileFor(depth: number, role: FaceRole): TileId {
  if (depth >= 2) return Tile.MagmaTop;
  if (role === "inward") return Tile.Stone;
  if (depth === 0) return role === "outward" ? Tile.GrassTop : Tile.GrassSide;
  return role === "outward" ? Tile.MagmaTop : Tile.MagmaSide;
}
